import React, { useState, useEffect } from 'react';
import { 
  Users, Building2, Shield, Plus, Edit, Trash2, Search, 
  ChevronDown, ChevronRight, UserCheck, Key, Store, RefreshCw 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import apiClient from '../lib/api';

import { MainLayout } from '../components/Layout';
// API wrapper
const api = {
  get: async (url) => {
    const res = await apiClient.get(url);
    return res.data;
  },
  post: async (url, data) => {
    const res = await apiClient.post(url, data);
    return res.data;
  },
  patch: async (url, data) => {
    const res = await apiClient.patch(url, data);
    return res.data;
  },
  delete: async (url) => {
    const res = await apiClient.delete(url);
    return res.data;
  }
};

// ==================== COMPONENTS ====================

const TabButton = ({ active, onClick, icon: Icon, label, count }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-all ${
      active 
        ? 'border-red-500 text-red-600 bg-red-50' 
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
    }`}
  >
    <Icon className="w-4 h-4" />
    <span className="font-medium">{label}</span>
    {count !== undefined && (
      <span className={`px-2 py-0.5 text-xs rounded-full ${
        active ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
      }`}>
        {count}
      </span>
    )}
  </button>
);

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-auto">
        <div className="sticky top-0 bg-white px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6">
          {children}
        </div>
      </div>
    </div>
  );
};

// ==================== STORES TAB ====================

const StoresTab = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    store_type: 'FILIAL',
    parent_store_id: '',
    cnpj: '',
    ie: '',
    address_street: '',
    address_number: '',
    address_complement: '',
    address_neighborhood: '',
    address_city: '',
    address_state: '',
    address_cep: '',
    phone: '',
    email: '',
    lat: null,
    lng: null
  });

  useEffect(() => {
    loadStores();
  }, []);

  const loadStores = async () => {
    try {
      const data = await api.get('/rbac/stores?include_inactive=true');
      setStores(data);
    } catch (error) {
      toast.error('Erro ao carregar lojas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStore) {
        await api.patch(`/rbac/stores/${editingStore.id}`, formData);
        toast.success('Loja atualizada com sucesso');
      } else {
        await api.post('/rbac/stores', formData);
        toast.success('Loja criada com sucesso');
      }
      setShowModal(false);
      setEditingStore(null);
      resetForm();
      loadStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar loja');
    }
  };

  const handleEdit = (store) => {
    setEditingStore(store);
    setFormData({
      name: store.name || '',
      store_type: store.store_type || 'FILIAL',
      parent_store_id: store.parent_store_id || '',
      cnpj: store.cnpj || '',
      ie: store.ie || '',
      address_street: store.address_street || '',
      address_number: store.address_number || '',
      address_complement: store.address_complement || '',
      address_neighborhood: store.address_neighborhood || '',
      address_city: store.address_city || '',
      address_state: store.address_state || '',
      address_cep: store.address_cep || '',
      phone: store.phone || '',
      email: store.email || '',
      lat: store.lat || null,
      lng: store.lng || null
    });
    setShowModal(true);
  };

  const handleDelete = async (store) => {
    if (!window.confirm(`Deseja desativar a loja "${store.name}"?`)) return;
    try {
      await api.delete(`/rbac/stores/${store.id}`);
      toast.success('Loja desativada');
      loadStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao desativar loja');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', store_type: 'FILIAL', parent_store_id: '', cnpj: '', ie: '',
      address_street: '', address_number: '', address_complement: '', address_neighborhood: '',
      address_city: '', address_state: '', address_cep: '', phone: '', email: '',
      lat: null, lng: null
    });
  };

  const matrizStores = stores.filter(s => s.store_type === 'MATRIZ' || !s.parent_store_id);

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Lojas</h3>
          <p className="text-sm text-gray-500">Gerencie matriz e filiais</p>
        </div>
        <Button onClick={() => { resetForm(); setEditingStore(null); setShowModal(true); }} className="bg-red-500 hover:bg-red-600">
          <Plus className="w-4 h-4 mr-2" /> Nova Loja
        </Button>
      </div>

      <div className="bg-white rounded-xl border shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Loja</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Tipo</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">CNPJ</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Cidade</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wide">Status</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wide">Filiais</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {stores.map(store => (
                <tr key={store.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-sm ${
                        store.store_type === 'MATRIZ' ? 'bg-gradient-to-br from-red-500 to-red-600 text-white' : 'bg-gradient-to-br from-blue-500 to-blue-600 text-white'
                      }`}>
                        <Building2 className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{store.name}</p>
                        {store.parent_store_id && (
                          <p className="text-xs text-slate-500">
                            Filial de: {stores.find(s => s.id === store.parent_store_id)?.name}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                      store.store_type === 'MATRIZ' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {store.store_type || 'MATRIZ'}
                    </span>
                  </td>
                  <td className="px-4 py-4 font-mono text-sm text-slate-600">{store.cnpj || '-'}</td>
                  <td className="px-4 py-4 text-sm text-slate-600">
                    {store.address_city ? `${store.address_city}/${store.address_state}` : '-'}
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                      store.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {store.is_active ? 'Ativa' : 'Inativa'}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 text-slate-700 font-semibold text-sm">
                      {store.children_count || 0}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleEdit(store)} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(store)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingStore ? 'Editar Loja' : 'Nova Loja'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome da Loja *</label>
              <Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
              <select
                value={formData.store_type}
                onChange={e => setFormData({...formData, store_type: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="MATRIZ">Matriz</option>
                <option value="FILIAL">Filial</option>
              </select>
            </div>
            {formData.store_type === 'FILIAL' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Loja Matriz</label>
                <select
                  value={formData.parent_store_id}
                  onChange={e => setFormData({...formData, parent_store_id: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Selecione...</option>
                  {matrizStores.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CNPJ</label>
              <Input value={formData.cnpj} onChange={e => setFormData({...formData, cnpj: e.target.value})} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">IE</label>
              <Input value={formData.ie} onChange={e => setFormData({...formData, ie: e.target.value})} />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Endereço</label>
              <Input value={formData.address_street} onChange={e => setFormData({...formData, address_street: e.target.value})} placeholder="Rua/Avenida" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Número</label>
              <Input value={formData.address_number} onChange={e => setFormData({...formData, address_number: e.target.value})} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Complemento</label>
              <Input value={formData.address_complement} onChange={e => setFormData({...formData, address_complement: e.target.value})} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bairro</label>
              <Input value={formData.address_neighborhood} onChange={e => setFormData({...formData, address_neighborhood: e.target.value})} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CEP</label>
              <Input value={formData.address_cep} onChange={e => setFormData({...formData, address_cep: e.target.value})} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
              <Input value={formData.address_city} onChange={e => setFormData({...formData, address_city: e.target.value})} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
              <Input value={formData.address_state} onChange={e => setFormData({...formData, address_state: e.target.value})} maxLength={2} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
              <Input value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <Input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
            </div>
            <div className="col-span-2 border-t pt-4 mt-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Coordenadas (para mapa de entregas)</label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Latitude</label>
                  <Input 
                    type="number" 
                    step="any"
                    value={formData.lat || ''} 
                    onChange={e => setFormData({...formData, lat: e.target.value ? parseFloat(e.target.value) : null})} 
                    placeholder="-23.5505"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Longitude</label>
                  <Input 
                    type="number" 
                    step="any"
                    value={formData.lng || ''} 
                    onChange={e => setFormData({...formData, lng: e.target.value ? parseFloat(e.target.value) : null})} 
                    placeholder="-46.6333"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={async () => {
                  if (formData.address_street && formData.address_number && formData.address_city) {
                    const address = `${formData.address_street}, ${formData.address_number}, ${formData.address_neighborhood || ''}, ${formData.address_city}, ${formData.address_state || 'SP'}, Brasil`;
                    try {
                      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}`);
                      const data = await response.json();
                      if (data && data[0]) {
                        setFormData({...formData, lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon)});
                      } else {
                        alert('Endereço não encontrado. Preencha manualmente.');
                      }
                    } catch (err) {
                      alert('Erro ao buscar coordenadas');
                    }
                  } else {
                    alert('Preencha o endereço primeiro');
                  }
                }}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Buscar coordenadas pelo endereço
              </button>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
            <Button type="submit" className="bg-red-500 hover:bg-red-600">Salvar</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// ==================== USERS TAB ====================

const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    email: '', name: '', phone: '', password: '', role: 'CAIXA', role_profile_id: '', store_ids: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersData, storesData, profilesData] = await Promise.all([
        api.get('/rbac/users?include_inactive=true'),
        api.get('/rbac/stores'),
        api.get('/rbac/profiles')
      ]);
      setUsers(usersData);
      setStores(storesData);
      setProfiles(profilesData);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (editingUser && !payload.password) delete payload.password;
      
      if (editingUser) {
        await api.patch(`/rbac/users/${editingUser.id}`, payload);
        toast.success('Usuário atualizado');
      } else {
        await api.post('/rbac/users', payload);
        toast.success('Usuário criado');
      }
      setShowModal(false);
      setEditingUser(null);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email, name: user.name, phone: user.phone || '', password: '',
      role: user.role, role_profile_id: user.role_profile_id || '',
      store_ids: user.store_access?.map(s => s.store_id) || []
    });
    setShowModal(true);
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Deseja desativar "${user.name}"?`)) return;
    try {
      await api.delete(`/rbac/users/${user.id}`);
      toast.success('Usuário desativado');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro');
    }
  };

  const resetForm = () => {
    setFormData({ email: '', name: '', phone: '', password: '', role: 'CAIXA', role_profile_id: '', store_ids: [] });
  };

  const filteredUsers = users.filter(u => 
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const roleLabels = {
    ADMIN_MASTER: 'Admin Master',
    GERENTE: 'Gerente',
    CAIXA: 'Caixa',
    COZINHA: 'Cozinha',
    ENTREGADOR: 'Entregador',
    CONTADOR: 'Contador'
  };

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Usuários</h3>
          <p className="text-sm text-gray-500">Gerencie os usuários do sistema</p>
        </div>
        <Button onClick={() => { resetForm(); setEditingUser(null); setShowModal(true); }} className="bg-red-500 hover:bg-red-600">
          <Plus className="w-4 h-4 mr-2" /> Novo Usuário
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Buscar por nome ou email..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="bg-white rounded-xl border">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuário</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cargo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Perfil</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lojas</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredUsers.map(user => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center text-red-600 font-medium">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{user.name}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 text-xs bg-gray-100 rounded-full">{roleLabels[user.role] || user.role}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{user.role_profile_name || '-'}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {user.store_access?.slice(0, 2).map((s, i) => (
                        <span key={i} className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded">{s.store_name}</span>
                      ))}
                      {user.store_access?.length > 2 && (
                        <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">+{user.store_access.length - 2}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      user.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {user.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => handleEdit(user)} className="p-1 text-gray-400 hover:text-blue-600">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(user)} className="p-1 text-gray-400 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingUser ? 'Editar Usuário' : 'Novo Usuário'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
              <Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <Input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} required disabled={!!editingUser} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
              <Input value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{editingUser ? 'Nova Senha (deixe vazio para manter)' : 'Senha *'}</label>
              <Input type="password" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} required={!editingUser} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cargo</label>
              <select value={formData.role} onChange={e => setFormData({...formData, role: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                {Object.entries(roleLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Perfil de Acesso</label>
              <select value={formData.role_profile_id} onChange={e => setFormData({...formData, role_profile_id: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                <option value="">Selecione...</option>
                {profiles.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Acesso às Lojas</label>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-auto border rounded-lg p-2">
                {stores.map(store => (
                  <label key={store.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.store_ids.includes(store.id)}
                      onChange={e => {
                        if (e.target.checked) {
                          setFormData({...formData, store_ids: [...formData.store_ids, store.id]});
                        } else {
                          setFormData({...formData, store_ids: formData.store_ids.filter(id => id !== store.id)});
                        }
                      }}
                      className="rounded border-gray-300 text-red-500"
                    />
                    <span className="text-sm">{store.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
            <Button type="submit" className="bg-red-500 hover:bg-red-600">Salvar</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// ==================== PROFILES TAB ====================

const ProfilesTab = () => {
  const [profiles, setProfiles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [formData, setFormData] = useState({ name: '', description: '', permission_ids: [] });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [profilesData, permsData, modulesData] = await Promise.all([
        api.get('/rbac/profiles?include_inactive=true'),
        api.get('/rbac/permissions'),
        api.get('/rbac/permissions/modules')
      ]);
      setProfiles(profilesData);
      setPermissions(permsData);
      setModules(modulesData);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProfile) {
        await api.patch(`/rbac/profiles/${editingProfile.id}`, formData);
        toast.success('Perfil atualizado');
      } else {
        await api.post('/rbac/profiles', formData);
        toast.success('Perfil criado');
      }
      setShowModal(false);
      setEditingProfile(null);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const handleEdit = (profile) => {
    setEditingProfile(profile);
    setFormData({
      name: profile.name,
      description: profile.description || '',
      permission_ids: profile.permissions?.map(p => p.id) || []
    });
    setShowModal(true);
  };

  const handleDelete = async (profile) => {
    if (!window.confirm(`Deseja desativar o perfil "${profile.name}"?`)) return;
    try {
      await api.delete(`/rbac/profiles/${profile.id}`);
      toast.success('Perfil desativado');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', permission_ids: [] });
  };

  const toggleModule = (module) => {
    setExpandedModules(prev => ({ ...prev, [module]: !prev[module] }));
  };

  const toggleModulePermissions = (module, checked) => {
    const modulePerms = permissions.filter(p => p.module === module).map(p => p.id);
    if (checked) {
      setFormData({ ...formData, permission_ids: [...new Set([...formData.permission_ids, ...modulePerms])] });
    } else {
      setFormData({ ...formData, permission_ids: formData.permission_ids.filter(id => !modulePerms.includes(id)) });
    }
  };

  const isModuleChecked = (module) => {
    const modulePerms = permissions.filter(p => p.module === module).map(p => p.id);
    return modulePerms.every(id => formData.permission_ids.includes(id));
  };

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Perfis de Acesso</h3>
          <p className="text-sm text-gray-500">Configure as permissões de cada perfil</p>
        </div>
        <Button onClick={() => { resetForm(); setEditingProfile(null); setShowModal(true); }} className="bg-red-500 hover:bg-red-600">
          <Plus className="w-4 h-4 mr-2" /> Novo Perfil
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {profiles.map(profile => (
          <div key={profile.id} className={`bg-white rounded-xl border p-4 ${!profile.is_active ? 'opacity-60' : ''}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">{profile.name}</h4>
                  {profile.is_system && (
                    <span className="text-xs text-gray-500">Perfil do sistema</span>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => handleEdit(profile)}
                  className="p-1 text-gray-400 hover:text-blue-600"
                  title={profile.is_system ? 'Editar permissões deste perfil do sistema' : 'Editar perfil'}
                >
                  <Edit className="w-4 h-4" />
                </button>
                {!profile.is_system && (
                  <button
                    onClick={() => handleDelete(profile)}
                    className="p-1 text-gray-400 hover:text-red-600"
                    title="Excluir perfil"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            <p className="text-sm text-gray-500 mb-3">{profile.description || 'Sem descrição'}</p>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">{profile.permissions?.length || 0} permissões</span>
              <span className="text-gray-500">{profile.user_count || 0} usuários</span>
            </div>
          </div>
        ))}
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingProfile ? 'Editar Perfil' : 'Novo Perfil'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Perfil *</label>
            <Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData({...formData, description: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg"
              rows={2}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Permissões</label>
            <div className="border rounded-lg max-h-64 overflow-auto">
              {modules.map(module => (
                <div key={module} className="border-b last:border-b-0">
                  <div
                    className="flex items-center gap-2 px-3 py-2 bg-gray-50 cursor-pointer hover:bg-gray-100"
                    onClick={() => toggleModule(module)}
                  >
                    {expandedModules[module] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    <input
                      type="checkbox"
                      checked={isModuleChecked(module)}
                      onChange={e => toggleModulePermissions(module, e.target.checked)}
                      onClick={e => e.stopPropagation()}
                      className="rounded border-gray-300 text-red-500"
                    />
                    <span className="font-medium text-sm">{module}</span>
                    <span className="text-xs text-gray-400">
                      ({permissions.filter(p => p.module === module).length})
                    </span>
                  </div>
                  {expandedModules[module] && (
                    <div className="px-8 py-2 space-y-1">
                      {permissions.filter(p => p.module === module).map(perm => (
                        <label key={perm.id} className="flex items-center gap-2 py-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.permission_ids.includes(perm.id)}
                            onChange={e => {
                              if (e.target.checked) {
                                setFormData({...formData, permission_ids: [...formData.permission_ids, perm.id]});
                              } else {
                                setFormData({...formData, permission_ids: formData.permission_ids.filter(id => id !== perm.id)});
                              }
                            }}
                            className="rounded border-gray-300 text-red-500"
                          />
                          <span className="text-sm">{perm.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
            <Button type="submit" className="bg-red-500 hover:bg-red-600">Salvar</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// ==================== MAIN PAGE ====================

export default function RBACPage() {
  const [activeTab, setActiveTab] = useState('stores');
  const [stats, setStats] = useState({ stores: 0, users: 0, profiles: 0 });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [stores, users, profiles] = await Promise.all([
        api.get('/rbac/stores'),
        api.get('/rbac/users'),
        api.get('/rbac/profiles')
      ]);
      setStats({
        stores: stores.length,
        users: users.length,
        profiles: profiles.length
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  return (

    <MainLayout>
    <div className="space-y-6" data-testid="rbac-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestão de Acessos</h1>
          <p className="text-gray-500">Gerencie lojas, usuários e permissões</p>
        </div>
      </div>

      {/* Stats Cards - Design Moderno */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-5 text-white shadow-lg shadow-red-500/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm font-medium">Lojas Cadastradas</p>
              <p className="text-3xl font-bold mt-1">{stats.stores}</p>
              <p className="text-red-200 text-xs mt-2">Matriz e filiais ativas</p>
            </div>
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <Building2 className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white shadow-lg shadow-blue-500/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Usuários Ativos</p>
              <p className="text-3xl font-bold mt-1">{stats.users}</p>
              <p className="text-blue-200 text-xs mt-2">Colaboradores com acesso</p>
            </div>
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <Users className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 text-white shadow-lg shadow-emerald-500/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-100 text-sm font-medium">Perfis de Acesso</p>
              <p className="text-3xl font-bold mt-1">{stats.profiles}</p>
              <p className="text-emerald-200 text-xs mt-2">Níveis de permissão</p>
            </div>
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <Shield className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border">
        <div className="flex border-b overflow-x-auto">
          <TabButton active={activeTab === 'stores'} onClick={() => setActiveTab('stores')} icon={Building2} label="Lojas" count={stats.stores} />
          <TabButton active={activeTab === 'users'} onClick={() => setActiveTab('users')} icon={Users} label="Usuários" count={stats.users} />
          <TabButton active={activeTab === 'profiles'} onClick={() => setActiveTab('profiles')} icon={Shield} label="Perfis" count={stats.profiles} />
        </div>

        <div className="p-6">
          {activeTab === 'stores' && <StoresTab />}
          {activeTab === 'users' && <UsersTab />}
          {activeTab === 'profiles' && <ProfilesTab />}
        </div>
      </div>
    </div>

    </MainLayout>

  );
}
